import React, { useEffect, useState, useMemo } from 'react';
import { getApiUrl } from '../utils/apiConfig';
import { db, auth } from '../services/firebase';
import { collection, addDoc, query, where, onSnapshot, doc, getDoc, serverTimestamp, orderBy, deleteDoc, writeBatch } from 'firebase/firestore';
import {
  Typography, Button, Card, CardContent, Box, Grid, List, ListItem, ListItemText, IconButton, Badge,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import styles from '../styles/Dashboard.module.css';
import { format } from 'date-fns';

const ReservarAreas = () => {
  const [areas, setAreas] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [dataSelecionada, setDataSelecionada] = useState(new Date());
  const [condominioId, setCondominioId] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [areaExpandida, setAreaExpandida] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = { uid: user.uid, ...userDoc.data() };
          setCurrentUser(userData);
          setCondominioId(userData.condominio_id);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Busca Áreas Comuns
  useEffect(() => {
    if (!condominioId) return;
    const q = query(collection(db, 'tb_areas_comuns'), where('condominio_id', '==', condominioId), orderBy('nome', 'asc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const areasList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAreas(areasList);
    });
    return () => unsubscribe();
  }, [condominioId]);

  // Busca Reservas
  useEffect(() => {
    if (!condominioId) return;
    const q = query(collection(db, 'tb_reservas'), where('condominio_id', '==', condominioId), orderBy('data_reserva', 'asc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const reservasList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReservas(reservasList);
    });
    return () => unsubscribe();
  }, [condominioId]);

  const handleAccordionChange = (areaId) => (event, isExpanded) => {
    setAreaExpandida(isExpanded ? areaId : null);
    setDataSelecionada(new Date()); // Reseta a data para a data atual ao trocar de área
  };

  const reservasPorArea = useMemo(() => {
    return reservas.reduce((acc, reserva) => {
      const { area_id } = reserva;
      if (!acc[area_id]) {
        acc[area_id] = [];
      }
      acc[area_id].push(reserva);
      return acc;
    }, {});
  }, [reservas]);

  const diasReservadosParaArea = useMemo(() => {
    if (!areaExpandida || !reservasPorArea[areaExpandida]) {
      return new Set();
    }
    const datas = new Set();
    reservasPorArea[areaExpandida].forEach(reserva => {
      // Adiciona uma verificação para garantir que a data da reserva é válida
      if (reserva.data_reserva && reserva.data_reserva.toDate) {
        const dataValida = reserva.data_reserva.toDate();
        datas.add(format(dataValida, 'yyyy-MM-dd'));
      }
    });
    return datas;
  }, [reservasPorArea, areaExpandida]);

  const handleReservar = async (area) => {
    if (!dataSelecionada) {
      alert('Por favor, selecione uma data para a reserva.');
      return;
    }
    if (!currentUser) {
      alert('Erro: usuário não identificado.');
      return;
    }

    const dataFormatada = format(dataSelecionada, 'yyyy-MM-dd');
    const isJaReservado = (reservasPorArea[area.id] || []).some(
      reserva => format(reserva.data_reserva.toDate(), 'yyyy-MM-dd') === dataFormatada
    );

    if (isJaReservado) {
      alert('Esta data já está reservada para esta área. Por favor, escolha outra data.');
      return;
    }

    try {
      const batch = writeBatch(db);
      let contaId = null;

      // Se a área tiver um custo, cria uma conta a receber
      if (area.custo_reserva && area.custo_reserva > 0) {
        const contaRef = doc(collection(db, 'tb_contas'));
        contaId = contaRef.id;

        batch.set(contaRef, {
          descricao: `Reserva: ${area.nome} | ${currentUser.nome} (Apto: ${currentUser.apto})`,
          valor: area.custo_reserva,
          tipo: 'a_receber',
          data_vencimento: dataSelecionada,
          status: 'pendente',
          condominio_id: condominioId,
          morador_id: currentUser.uid,
          created_at: serverTimestamp(),
          anexo_url: '',
          anexo_path: '',
        });
      }

      // Cria a reserva
      const reservaRef = doc(collection(db, 'tb_reservas'));
      batch.set(reservaRef, {
        area_id: area.id,
        area_nome: area.nome,
        condominio_id: condominioId,
        morador_id: currentUser.uid,
        morador_nome: currentUser.nome,
        morador_apto: currentUser.apto,
        data_reserva: dataSelecionada,
        status: 'confirmada',
        created_at: serverTimestamp(),
        conta_id: contaId, // Link para a conta financeira
      });

      await batch.commit();

      alert(`Reserva para "${area.nome}" no dia ${format(dataSelecionada, 'dd/MM/yyyy')} realizada com sucesso!`);
      setDataSelecionada(new Date());
    } catch (error) {
      console.error("Erro ao criar reserva:", error);
      alert('Erro ao criar reserva e lançamento financeiro: ' + error.message);
    }
  };

  const handleCancelarReserva = async (reserva) => {
    if (window.confirm('Tem certeza que deseja cancelar esta reserva?')) {
      try {
        const token = await auth.currentUser.getIdToken();
        const response = await fetch(`${getApiUrl()}/api/reservas/${reserva.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Erro ao cancelar reserva.');
        }
        alert('Reserva cancelada com sucesso.');
      } catch (error) {
        console.error("Erro ao cancelar reserva:", error);
        alert('Erro ao cancelar reserva: ' + error.message);
      }
    }
  };

  const podeCancelar = (reserva) => {
    if (!currentUser) return false;
    const isAdmin = ['sindico', 'sub_sindico', 'conselheiro'].includes(currentUser.papel);
    return isAdmin || reserva.morador_id === currentUser.uid;
  };

  const renderDiaComBadge = (props) => {
    const { day, outsideCurrentMonth } = props;
    // Converte a data para um objeto Date, pois ela pode vir como string.
    const date = day instanceof Date ? day : new Date(day);

    // Verificação de segurança para datas inválidas.
    if (isNaN(date)) {
      console.error('Invalid date value detected after conversion:', day);
      return <PickersDay {...props} disabled />; // Renderiza como desabilitado para evitar crash
    }

    const diaFormatado = format(date, 'yyyy-MM-dd');
    const isReservado = diasReservadosParaArea.has(diaFormatado);

    return (
      <Badge key={day.toString()} overlap="circular" badgeContent={isReservado && !outsideCurrentMonth ? <EventBusyIcon sx={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.4)' }} /> : undefined}>
        <PickersDay {...props} disabled={isReservado || props.disabled} />
      </Badge>
    );
  };
  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Reservar Áreas Comuns</Typography>

        {areas.map((area) => (
          <Accordion key={area.id} sx={{ mb: 2 }} expanded={areaExpandida === area.id} onChange={handleAccordionChange(area.id)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography variant="h6">{area.nome}</Typography>
                <Typography variant="caption" color="text.secondary" component="span" sx={{ mr: 2 }}>Capacidade: {area.capacidade_maxima || 'N/A'}</Typography>
                <Typography variant="caption" color="text.secondary" component="span">Custo: {area.custo_reserva > 0 ? `R$ ${area.custo_reserva.toFixed(2)}` : 'Gratuito'}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Card da Esquerda: Fazer Nova Reserva */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      {area.regras && (
                        <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}><strong>Regras:</strong> {area.regras}</Typography>
                      )}
                      <Typography variant="h6" gutterBottom>Fazer Nova Reserva</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                        {areaExpandida === area.id && currentUser && (
                          <StaticDatePicker
                            key={area.id} // Força a remontagem do componente ao trocar de área
                            value={dataSelecionada}
                            onChange={(novaData) => {
                              // Garante que o estado nunca seja nulo
                              if (novaData) {
                                setDataSelecionada(novaData);
                              }
                            }}
                            disablePast
                            slots={{
                              day: renderDiaComBadge,
                            }}
                          />
                        )}
                        <Button
                          variant="contained"
                          onClick={() => handleReservar(area)}
                          disabled={!dataSelecionada}
                          fullWidth
                        >
                          Reservar {area.nome}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Card da Direita: Datas Reservadas */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Datas Reservadas</Typography>
                      <List dense>
                        {(reservasPorArea[area.id] || []).map(reserva => (
                          <ListItem key={reserva.id} secondaryAction={
                            podeCancelar(reserva) && (
                              <IconButton edge="end" aria-label="cancelar" onClick={() => handleCancelarReserva(reserva)}>
                                <DeleteIcon />
                              </IconButton>
                            )
                          }>
                            <ListItemText
                              primary={reserva.data_reserva?.toDate ? format(reserva.data_reserva.toDate(), 'dd/MM/yyyy') : 'Data inválida'}
                              secondary={`Reservado por: ${reserva.morador_nome || 'Não identificado'} | Apto: ${reserva.morador_apto || 'N/A'}`}
                            />
                          </ListItem>
                        ))}
                        {(reservasPorArea[area.id] || []).length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Nenhuma reserva para esta área.</Typography>
                        )}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
        {areas.length === 0 && (
          <Card className={styles.card}>
            <CardContent>
              <Typography>Nenhuma área comum disponível para reserva no momento.</Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default ReservarAreas;
